import React, { useMemo } from 'react';
import type { Account, Contact } from '@axiom/validation';
import type { FormPropsChildren } from '@axiom/ui';
import {
  Banner,
  Button,
  CondensedMedium,
  Dropdown,
  Flashy,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  IconButton,
  Input,
  Layout,
  LayoutItem,
  Radio,
  useApi,
} from '@axiom/ui';

import { AccountsApi } from '../../api/accounts-api';
import type { InterviewAttendeesAndLinkModal } from '../../schema/interview-attendees-and-link-modal-schema';
import { InterviewAttendeesAndLinkModalUtil } from '../../utils/interview-attendees-and-link-modal-util';

export type InterviewAttendeesAndLinkModalFormBodyProps = {
  accountId: Account['id'];
  accountContacts: Contact[];
  talentName: string;
  values: FormPropsChildren<InterviewAttendeesAndLinkModal>['values'];
  setValues: FormPropsChildren<InterviewAttendeesAndLinkModal>['setValues'];
  zoomDisabled?: boolean;
};

export const InterviewAttendeesAndLinkModalFormBody = ({
  accountId,
  accountContacts,
  talentName,
  values,
  setValues,
  zoomDisabled = false,
}: InterviewAttendeesAndLinkModalFormBodyProps) => {
  const [{ data: meetingLinkTypes }] = useApi(
    AccountsApi.readMeetingLinkTypes(accountId)
  );
  const adHocContacts = values.adHocContacts ?? [];
  const contacts = values.contacts ?? [];

  const contactOptions = useMemo(
    () =>
      [...accountContacts]
        .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''))
        .map(c => ({
          label: `${c.fullName} (${c.email})`,
          value: c.id,
        })),
    [accountContacts]
  );

  const adhocEmails = adHocContacts.map(c => c.email).filter(Boolean);
  const contactEmails = accountContacts
    .filter(c => contacts.includes(c.id))
    .map(c => c.email);
  const showDomainWarning =
    adhocEmails.length > 0 &&
    InterviewAttendeesAndLinkModalUtil.checkDomainMismatch(
      contactEmails,
      adhocEmails
    );

  return (
    <div data-test="INTERVIEW_ATTENDEES_AND_LINK_MODAL_FORM_BODY">
      <FormGroup
        name="attendees"
        label="Who should we invite to the interview?"
        description={`Select or add additional attendees below. All contacts selected will
          receive an invitation to join ${talentName}'s interview.`}
      >
        {accountContacts.length > 0 ? (
          <Gutter bottom="16px">
            <Dropdown
              name="contacts"
              displayKey="label"
              valueKey="value"
              options={contactOptions}
            />
          </Gutter>
        ) : null}
        <Gutter horizontal="4px">
          {adHocContacts.some(c => c.readonly) ? (
            <Gutter bottom="12px">
              {adHocContacts.map((contact, idx) =>
                contact.readonly ? (
                  <Gutter bottom="8px" key={`adHocContacts_${String(idx)}`}>
                    <Layout
                      name="ADHOC_READONLY_ROW"
                      direction="horizontal"
                      position="middle"
                    >
                      <LayoutItem fluid>
                        <CondensedMedium
                          name={`ADHOC_READONLY_NAME_${String(idx)}`}
                        >
                          {contact.firstName} {contact.lastName} (
                          {contact.email})
                        </CondensedMedium>
                      </LayoutItem>
                      <LayoutItem>
                        <IconButton
                          name={`REMOVE_ADHOC_${String(idx)}`}
                          icon="trash"
                          pattern="secondary"
                          variation="minimal"
                          onClick={() => {
                            void setValues(prev => {
                              const updated = [...(prev.adHocContacts ?? [])];
                              updated.splice(idx, 1);
                              return { ...prev, adHocContacts: updated };
                            });
                          }}
                        />
                      </LayoutItem>
                    </Layout>
                  </Gutter>
                ) : null
              )}
            </Gutter>
          ) : null}
          <Grid>
            {adHocContacts.map((contact, idx) =>
              contact.readonly ? null : (
                <GridRow
                  key={`adHocContacts_${String(idx)}`}
                  name="ADHOC_ROW"
                  stretched
                  columns={4}
                  gutterBottom="12px"
                >
                  <GridColumn largeScreenWidth={3} smallScreenWidth={12}>
                    <Input
                      name={`adHocContacts.${String(idx)}.firstName`}
                      label="First Name"
                      placeholder="Entered Text"
                    />
                  </GridColumn>
                  <GridColumn largeScreenWidth={3} smallScreenWidth={12}>
                    <Input
                      name={`adHocContacts.${String(idx)}.lastName`}
                      label="Last Name"
                      placeholder="Entered Text"
                    />
                  </GridColumn>
                  <GridColumn largeScreenWidth={5} smallScreenWidth={10}>
                    <Input
                      name={`adHocContacts.${String(idx)}.email`}
                      label="Email"
                      placeholder="Entered Text"
                    />
                  </GridColumn>
                  <GridColumn largeScreenWidth={1} smallScreenWidth={2}>
                    <Gutter top="40px">
                      <IconButton
                        name={`REMOVE_ADHOC_${String(idx)}`}
                        icon="trash"
                        pattern="secondary"
                        variation="minimal"
                        onClick={() => {
                          void setValues(prev => {
                            const updated = [...(prev.adHocContacts ?? [])];
                            updated.splice(idx, 1);
                            return { ...prev, adHocContacts: updated };
                          });
                        }}
                      />
                    </Gutter>
                  </GridColumn>
                </GridRow>
              )
            )}
          </Grid>
          {showDomainWarning ? (
            <Gutter bottom="16px" top="4px">
              <Banner
                name="DOMAIN_MISMATCH_WARNING"
                type="warning"
                impact="high"
              >
                <Flashy bold>Check the email domain!</Flashy> The email domains
                don't match, make sure they're right before proceeding.
              </Banner>
            </Gutter>
          ) : null}

          <Button
            name="ADD_ADHOC_BUTTON"
            pattern="secondary"
            variation="minimal"
            onClick={() => {
              void setValues(prev => ({
                ...prev,
                adHocContacts: [
                  ...(prev.adHocContacts ?? []),
                  { firstName: '', lastName: '', email: '', readonly: false },
                ],
              }));
            }}
          >
            ADD NEW +
          </Button>
        </Gutter>
      </FormGroup>
      <Gutter bottom="16px" />
      <Gutter horizontal="4px">
        {meetingLinkTypes.teams ? (
          <Gutter bottom="16px">
            <Banner
              name="PRIOR_NON_ZOOM_INTERVIEW_BANNER"
              type="warning"
              impact="high"
            >
              At least one prior interview with this client did not use Zoom.
            </Banner>
          </Gutter>
        ) : null}
        <Radio
          label="Meeting link"
          name="meetingLinkType"
          option="ZOOM"
          displayValue="Automatic Zoom link sent in email invitation"
          disabled={zoomDisabled}
        />
        <Gutter bottom="16px" />
        <Radio
          name="meetingLinkType"
          option="MANUAL"
          displayValue="Manual link"
        />
        <Gutter bottom="8px" />
        <Gutter left="32px">
          <Input
            name="meetingLink"
            placeholder="https://"
            onChange={value => {
              if (value && values.meetingLinkType !== 'MANUAL') {
                void setValues(prev => ({
                  ...prev,
                  meetingLinkType: 'MANUAL',
                }));
              }
            }}
          />
        </Gutter>
      </Gutter>
    </div>
  );
};
